import { useState, useMemo } from 'react';
import { Search, Filter, X, Phone, Eye, Plus } from 'lucide-react';
import CallList from '../../../../components/features/support/calls/CallsList';
import CallDetailsModal from '../../../../components/features/support/calls/CallDetailsModal';
import CallSaveModal from '../../../../components/features/support/calls/CallSaveModal'; // Nouveau modal
import TicketSaveModal from '../../../../components/features/support/tickets/TicketSaveModal';
import MenuModal, { Menu } from '../../../../components/ui/MenuModal';
import useCalls from '../../../../hooks/support/calls/useCalls';
import { Call } from '../../../../types/features/support/Call';
import PageListLayout from '../../../../layouts/PageListLayout';
import { alertSuccess } from '../../../../helpers/alertError';
import { ticketService } from '../../../../services/support/ticketService';

export default function CallsPage() {
  const { calls, loading, pagination, fetchCalls, createCall } = useCalls();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDirection, setSelectedDirection] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  // États de sélection distincts pour éviter les chevauchements
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [callToView, setCallToView] = useState<Call | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  
  // États pour les modals de création
  const [callSaveModalOpen, setCallSaveModalOpen] = useState(false);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [preselectedClientId, setPreselectedClientId] = useState<string | undefined>(undefined);

  const filteredCalls = useMemo(() => {
    let result = calls;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.client_name?.toLowerCase().includes(searchLower) ||
        c.client_phone?.includes(searchTerm) ||
        c.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    if (selectedDirection) {
      result = result.filter(c => c.direction === selectedDirection);
    }

    return result;
  }, [calls, searchTerm, selectedDirection]);

  const stats = {
    total: calls.length,
    entrant: calls.filter(c => c.direction === 'entrant').length,
    sortant: calls.filter(c => c.direction === 'sortant').length,
    avecNotes: calls.filter(c => c.notes).length
  };

  const clearFilters = () => {
    setSelectedDirection('');
    setSearchTerm('');
  };

  const hasActiveFilters = selectedDirection !== '' || searchTerm.trim() !== '';

  const getActionsMenu = (call: Call): Menu[] => {
    return [
      {
        label: 'Voir détails',
        icon: Eye,
        onClick: () => {
          setCallToView(call);
          setDetailsModalOpen(true);
          setSelectedCall(null);
        }
      },
     
      {
        label: 'Rappeler',
        icon: Phone,
        onClick: () => {
          if (call.client_phone) {
            alertSuccess(`Relance téléphonique vers ${call.client_phone}`);
          } else {
            alertSuccess(`Aucun numéro disponible pour ce client`);
          }
          setSelectedCall(null);
        }
      }
    ];
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchCalls(page, pagination.perPage);
  };

  const handleCallSaved = () => {
    setCallSaveModalOpen(false);
    fetchCalls(currentPage, pagination.perPage);
    alertSuccess('Appel enregistré avec succès');
  };

  return (
    <PageListLayout title="Historique des appels">
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Appels</h1>
              <p className="text-sm text-gray-500 mt-1">
                Consultez l'historique des appels entrants et sortants
              </p>
            </div>
            <button
              onClick={() => setCallSaveModalOpen(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
            >
              <Phone size={18} />
              Enregistrer un appel
            </button>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
              <p className="text-xs text-gray-700/70">Total</p>
              <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
              <p className="text-xs text-green-700/70">Appels entrants</p>
              <p className="text-2xl font-bold text-green-700">{stats.entrant}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
              <p className="text-xs text-blue-700/70">Appels sortants</p>
              <p className="text-2xl font-bold text-blue-700">{stats.sortant}</p>
            </div>
          </div>

          {/* Barre de Recherche */}
          <div className="relative mt-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par client, numéro ou tag..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500/50"
            />
          </div>

          {/* Section Filtres */}
          <div className="mt-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              <Filter size={18} /> Filtrer
              {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
            </button>

            {showFilters && (
              <div className="bg-gray-50 rounded-xl p-4 mt-3 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-700">Filtres</h3>
                  {hasActiveFilters && (
                    <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
                      <X size={14} /> Effacer tout
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Direction</label>
                    <select
                      value={selectedDirection}
                      onChange={(e) => setSelectedDirection(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Tous</option>
                      <option value="entrant">Appel entrant</option>
                      <option value="sortant">Appel sortant</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Liste des appels */}
      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : (
          <>
            <CallList
              calls={filteredCalls}
              onAction={(call) => {
                setSelectedCall(call);
              }}
              selectable={false}
            />
            
            {/* Pagination */}
            {pagination.total > 0 && (
              <div className="flex justify-between items-center mt-6">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-gray-600 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Précédent
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} sur {pagination.lastPage} ({pagination.total} appels)
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.lastPage}
                  className="px-4 py-2 text-gray-600 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Menu modal volant (Actions d'un appel) */}
      {selectedCall && (
        <MenuModal
          title={`Appel - ${selectedCall.client.full_name || 'Inconnu'}`}
          isOpen={true}
          onClose={() => setSelectedCall(null)}
          icon={null}
          menu={getActionsMenu(selectedCall)}
        />
      )}

      {/* Modal d'affichage des détails de l'appel */}
      {callToView && (
        <CallDetailsModal
          isOpen={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setCallToView(null);
          }}
          call={callToView}
        />
      )}

      {/* Modal de création d'appel */}
      <CallSaveModal
        isOpen={callSaveModalOpen}
        onClose={() => setCallSaveModalOpen(false)}
        onSuccess={handleCallSaved}
      />

      {/* Modal de création de ticket provoqué depuis un appel */}
      <TicketSaveModal
        isOpen={ticketModalOpen}
        onClose={() => {
          setTicketModalOpen(false);
          setPreselectedClientId(undefined);
        }}
        onSuccess={() => {
          setTicketModalOpen(false);
          alertSuccess('Ticket créé avec succès depuis l\'appel');
          setPreselectedClientId(undefined);
          
        }}
        initialData={preselectedClientId ? { client_id: preselectedClientId } as any : undefined}
     
     />
    </PageListLayout>
  );
}